import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AppModule, Capabilities } from '@overseer/contracts';
import { meQuery } from '../api/me';
import { applyGeneral, preferencesQuery, resetPreferenceSession } from '../preferences';
import * as session from './session';

/**
 * Session state for the whole app.
 *
 * Deliberately small: signed in or not, and who. **Capabilities do not live
 * here** — they come from `GET /v1/me` through TanStack Query, because they are
 * server state, and putting them in a context invites the client to compute
 * them, which is the mistake §4.3 documents.
 */
interface AuthState {
  status: 'loading' | 'signed-in' | 'signed-out' | 'new-password-required';
  displayName?: string;
  signIn: (username: string, password: string) => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [status, setStatus] = useState<AuthState['status']>('loading');
  const [displayName, setDisplayName] = useState<string>();
  const queryClient = useQueryClient();

  /*
   * The person's saved language, theme and module, applied once per sign-in.
   *
   * Once, not on every change of the cached answer: this tab writes its own
   * choices into that cache, and re-applying would replay them out of order.
   */
  const preferences = useQuery({ ...preferencesQuery(), enabled: status === 'signed-in' });
  const applied = useRef(false);

  useEffect(() => {
    if (status !== 'signed-in') {
      applied.current = false;

      return;
    }

    if (preferences.data && !applied.current) {
      applied.current = true;
      applyGeneral(preferences.data.general);
    }
  }, [status, preferences.data]);

  const loadIdentity = useCallback(async () => {
    setDisplayName(await session.displayName());
  }, []);

  useEffect(() => {
    // Restores an existing session on load. `hasSession` goes through
    // fetchAuthSession, so an expired access token is refreshed here rather
    // than surfacing as a 401 on the first request.
    void (async () => {
      if (await session.hasSession()) {
        await loadIdentity();
        setStatus('signed-in');
      } else {
        setStatus('signed-out');
      }
    })();
  }, [loadIdentity]);

  const signIn = useCallback(
    async (username: string, password: string) => {
      const outcome = await session.signIn(username, password);

      if (outcome.status === 'new-password-required') {
        setStatus('new-password-required');

        return;
      }

      await loadIdentity();
      setStatus('signed-in');
    },
    [loadIdentity],
  );

  const completeNewPassword = useCallback(
    async (newPassword: string) => {
      await session.completeNewPassword(newPassword);
      await loadIdentity();
      setStatus('signed-in');
    },
    [loadIdentity],
  );

  const signOut = useCallback(async () => {
    await session.signOut();
    // Clears cached server state on the way out, so the next user of this
    // browser does not see the previous one's data flash up before refetching.
    queryClient.clear();
    resetPreferenceSession();
    setDisplayName(undefined);
    setStatus('signed-out');
  }, [queryClient]);

  const value = useMemo<AuthState>(
    () => ({ status, displayName, signIn, completeNewPassword, signOut }),
    [status, displayName, signIn, completeNewPassword, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthState => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }

  return context;
};

/** `GET /v1/me`, but only once there is a session to ask with. */
export const useMe = () => {
  const { status } = useAuth();

  return useQuery({ ...meQuery(), enabled: status === 'signed-in' });
};

/**
 * `GET /v1/me/preferences`, once there is a session. `isPending` stays true while
 * signed out, so a caller waiting on it waits rather than acting on nothing.
 */
export const usePreferences = () => {
  const { status } = useAuth();

  return useQuery({ ...preferencesQuery(), enabled: status === 'signed-in' });
};

/**
 * What the signed-in user may do, from the server.
 *
 * Returns every capability as `false` while `/v1/me` is in flight, so a control
 * cannot flash into view and then disappear. Hiding a button is presentation
 * only — the API enforces the same rules, and a capability being true is
 * permission to render, not permission to act.
 */
export const useCapabilities = (): Capabilities & { loaded: boolean } => {
  const { data } = useMe();

  return {
    loaded: Boolean(data),
    canSeeCosts: data?.capabilities.canSeeCosts ?? false,
    canEditEmployees: data?.capabilities.canEditEmployees ?? false,
    canSeeEmployeeRecords: data?.capabilities.canSeeEmployeeRecords ?? false,
    canSeeReports: data?.capabilities.canSeeReports ?? false,
    canManageReaders: data?.capabilities.canManageReaders ?? false,
    canManageReferenceData: data?.capabilities.canManageReferenceData ?? false,
    canRecalculate: data?.capabilities.canRecalculate ?? false,
    canCorrectAttendance: data?.capabilities.canCorrectAttendance ?? false,
    canCorrectPastAttendance: data?.capabilities.canCorrectPastAttendance ?? false,
    canManagePayroll: data?.capabilities.canManagePayroll ?? false,
    canEditPayroll: data?.capabilities.canEditPayroll ?? false,
    canClosePayroll: data?.capabilities.canClosePayroll ?? false,
    canRegisterCards: data?.capabilities.canRegisterCards ?? false,
    canManageUsers: data?.capabilities.canManageUsers ?? false,
    canManageSettings: data?.capabilities.canManageSettings ?? false,
    canReadLogs: data?.capabilities.canReadLogs ?? false,
    canEditHotelSettings: data?.capabilities.canEditHotelSettings ?? false,
    canQualifyHotelNights: data?.capabilities.canQualifyHotelNights ?? false,
    canEditGastronomySettings: data?.capabilities.canEditGastronomySettings ?? false,
    canEditCateringSettings: data?.capabilities.canEditCateringSettings ?? false,
    canSettleCatering: data?.capabilities.canSettleCatering ?? false,
    canEditCashierSettings: data?.capabilities.canEditCashierSettings ?? false,
    canReportCash: data?.capabilities.canReportCash ?? false,
    canAcceptCashReports: data?.capabilities.canAcceptCashReports ?? false,
    canKeepMainDesk: data?.capabilities.canKeepMainDesk ?? false,
    canPayOut: data?.capabilities.canPayOut ?? false,
    canSeeCashier: data?.capabilities.canSeeCashier ?? false,
    canSeeCashOverview: data?.capabilities.canSeeCashOverview ?? false,
    canAssignGastronomyRemarks: data?.capabilities.canAssignGastronomyRemarks ?? false,
  };
};

/**
 * Whether the signed-in user may read everything and change nothing.
 *
 * Its own hook rather than a field on `useCapabilities`, whose contract is "every
 * field is a capability" — `loaded` is already the odd one out there.
 *
 * ⚠️ Defaults to **false** while `/v1/me` is in flight, which is the opposite of
 * the capability default and deliberate. A capability defaults false so a button
 * cannot flash into view; this defaults false so every button does not vanish
 * for the second the request takes. Both directions err towards the page looking
 * the same before and after loading.
 *
 * Most write controls already disappear on their own, because every write
 * capability is false for such a user. This is for the screens that render an
 * editor with no capability gate at all.
 */
export const useIsReadOnly = (): boolean => {
  const { data } = useMe();

  return data?.isReadOnly ?? false;
};

/**
 * Which applications the signed-in user may open.
 *
 * `loaded` matters more here than it does for capabilities. An unloaded
 * capability hides a button, which is harmless; an unloaded module list is
 * indistinguishable from "this account has none", and acting on it would send
 * somebody to the "no application" screen for the second or two `/v1/me` is in
 * flight. **Every caller must check `loaded` before believing `modules`.**
 */
export const useModules = (): { modules: AppModule[]; loaded: boolean } => {
  const { data } = useMe();

  return { modules: data?.modules ?? [], loaded: Boolean(data) };
};
